import { calculateTrigonmetricDistance, sortByDbscanClusters } from "@nova/calculate-geospatial"
import { formatDistance } from "@nova/format"
import {
    ColorSwatchCategory,
    DistanceUnit,
    hexColorSwatches,
    HotspotPointTypes,
    HotspotProperties,
    hotspotSizes,
    HotspotStatus,
    HotspotWithContext,
    HotspotWithDistance,
    HotspotWithLabel,
    InfraredFlightData,
} from "@nova/constants"
import { getHotspotPerimeterDistances } from "@nova/data-create-hotspots-kml"
import { MinMax, Project } from "@nova/protobuf"
import { decodeNumberToColor } from "@nova/serializer/src/client/util"
import { convertHexStringToRGBArray } from "@nova/util-strings"

export const getHotspotFillColor = (
    d: Omit<HotspotWithDistance, "color">,
    hotspotProperties: HotspotProperties | undefined,
    opacity = 255,
    distanceToPerimeterIsEnabled = true
): [number, number, number, number] => {
    const hexColor = hotspotProperties?.pointColor
    const color = hexColor ? convertHexStringToRGBArray(decodeNumberToColor(hexColor)) : null

    const status = d.status
    if (status === HotspotStatus.Out) {
        //green
        return [0, 200, 0, opacity]
    }

    //pick the color based on its distance to the perimeter
    const distanceRange = hotspotProperties?.perimeterDistance?.range || []
    let distanceColor = color
    const hotspotDistance = d.distanceToPerimeter || undefined
    if (distanceToPerimeterIsEnabled) {
        for (const range of distanceRange) {
            if (hotspotDistance && hotspotDistance < range.distance) {
                distanceColor = convertHexStringToRGBArray(decodeNumberToColor(range.color))
                break
            }
        }
    }
    if (distanceRange.length > 0 && !hotspotDistance) {
        distanceColor = convertHexStringToRGBArray(decodeNumberToColor(distanceRange[0].color))
    }
    if (!distanceColor) {
        //red
        return [227, 71, 82, opacity]
    }

    if (distanceColor.length === 4) return distanceColor
    return [...distanceColor, opacity]
}

export const getHotspotFillColorAndLabel = (
    d: HotspotWithDistance,
    hotspotProperties: InfraredFlightData | undefined,
    opacity = 255,
    distanceToPerimeterIsEnabled = true
): [string, [number, number, number, number]] => {
    const hexColor = hotspotProperties?.pointColor
    const color = hexColor ? convertHexStringToRGBArray(decodeNumberToColor(hexColor)) : null

    // Make this a switch case when hex mode is fixed in PDF preview
    if (hotspotProperties?.pointType === HotspotPointTypes.Heat) {
        return ["Heat", [0, 0, 0, 0]]
    }

    const status = d.status
    if (status === HotspotStatus.Out) {
        //green
        return [`Out Hotspot`, [0, 200, 0, opacity]]
    }

    //pick the color based on its distance to the perimeter
    const distanceRange = hotspotProperties?.perimeterDistance?.range || []
    let distanceColor = ["Hotspot", color]
    const hotspotDistance = d.distanceToPerimeter || undefined
    if (distanceToPerimeterIsEnabled) {
        for (const range of distanceRange) {
            if (hotspotDistance && hotspotDistance < range.distance) {
                distanceColor = [
                    `Hotspot < ${formatDistance({
                        value: range.distance,
                        unit: hotspotProperties?.perimeterDistance?.unit as DistanceUnit,
                    })}`,
                    convertHexStringToRGBArray(decodeNumberToColor(range.color)),
                ]
                break
            }
        }
    }
    if (distanceRange.length > 0 && !hotspotDistance) {
        distanceColor = [
            `Hotspot < ${formatDistance({
                value: distanceRange[0].distance,
                unit: hotspotProperties?.perimeterDistance?.unit as DistanceUnit,
            })}`,
            convertHexStringToRGBArray(decodeNumberToColor(distanceRange[0].color)),
        ]
    }
    if (!distanceColor[1]) {
        //red
        return ["Hotspot", [227, 71, 82, opacity]]
    }

    if (distanceColor[1]?.length === 4)
        return distanceColor as [string, [number, number, number, number]]
    return [
        distanceColor[0] as string,
        [...(distanceColor[1] as [number, number, number]), opacity],
    ]
}

export const filterHotspotsBySensitivity = ({
    sensitivity,
    hotspots,
}: {
    sensitivity?: number
    hotspots: HotspotWithContext[]
}) => {
    return hotspots.filter((hotspot) => {
        const confidence = hotspot.confidence
        let isWithinSensitivity = true
        if (confidence && sensitivity) {
            isWithinSensitivity = confidence >= sensitivity
        }
        return isWithinSensitivity
    })
}

// also applies colors and labels
export const filterHotspotsByDistance = ({
    distances,
    properties,
    distanceToPerimeterIsEnabled,
    hotspots,
    // needsLabel = false,
}: {
    distances: Record<string, number | undefined>
    distanceToPerimeterIsEnabled: boolean
    properties: HotspotProperties
    hotspots: HotspotWithContext[]
    needsLabel?: boolean
}) => {
    if (!distanceToPerimeterIsEnabled) {
        const defaultHotspotColor = convertHexStringToRGBArray(
            properties.pointColor
                ? decodeNumberToColor(properties.pointColor)
                : hexColorSwatches[ColorSwatchCategory.Hotspot][0]
        )
        return hotspots.map((hotspot) => ({
            ...hotspot,
            color: [...defaultHotspotColor],
        })) as HotspotWithDistance[]
    }

    const ranges = properties.perimeterDistance?.range

    return (
        hotspots.filter((hotspot) => {
            const distanceToPerimeter = distances[hotspot.id]

            //filter based on the values in the Distance to Perimeter tool
            let isWithinPerimeter = true

            if (ranges?.length && distanceToPerimeter && distanceToPerimeterIsEnabled) {
                const maxDistanceRange = ranges[ranges.length - 1]
                isWithinPerimeter = distanceToPerimeter < maxDistanceRange.distance
            }

            return isWithinPerimeter
        }) as HotspotWithDistance[]
    ).map((hotspot) => {
        const hotspotWithDistance: HotspotWithDistance = {
            ...hotspot,
            distanceToPerimeter: distances[hotspot.id],
        } as HotspotWithDistance

        const color = getHotspotFillColor(
            hotspotWithDistance,
            properties,
            undefined,
            distanceToPerimeterIsEnabled
        )
        hotspotWithDistance.color = color

        return hotspotWithDistance
    })
}

const getBoundingBoxArea = (
    polygon: { latitude: number; longitude: number }[]
): number | undefined => {
    if (polygon.length < 3) return undefined
    let minLat = polygon[0].latitude
    let maxLat = polygon[0].latitude
    let minLng = polygon[0].longitude
    let maxLng = polygon[0].longitude
    for (let i = 1; i < polygon.length; i++) {
        const { latitude, longitude } = polygon[i]
        if (latitude < minLat) minLat = latitude
        if (latitude > maxLat) maxLat = latitude
        if (longitude < minLng) minLng = longitude
        if (longitude > maxLng) maxLng = longitude
    }
    const widthMeters = calculateTrigonmetricDistance(
        { latitude: minLat, longitude: minLng },
        { latitude: minLat, longitude: maxLng }
    )
    const heightMeters = calculateTrigonmetricDistance(
        { latitude: minLat, longitude: minLng },
        { latitude: maxLat, longitude: minLng }
    )
    return widthMeters * heightMeters
}

export const filterHotspotsByGraphs = ({
    hotspotsFilteredBySensitivity,
    hotspotsFilteredBySensitivityAndDistance: hotspots,
    sizeRange,
    temperatureRange,
}: {
    hotspotsFilteredBySensitivity: HotspotWithContext[]
    hotspotsFilteredBySensitivityAndDistance: HotspotWithDistance[]
    sizeRange?: MinMax
    temperatureRange?: MinMax
}) => {
    if (!sizeRange) sizeRange = { min: -Infinity, max: Infinity }

    const areas = hotspotsFilteredBySensitivity
        .map((hotspot) => getBoundingBoxArea(hotspot.polygon) ?? hotspot.area)
        .filter((area) => area !== undefined)
    const mean = areas.length > 0 ? areas.reduce((sum, area) => sum + area, 0) / areas.length : 0
    const variance =
        areas.length > 0
            ? areas.reduce((sum, area) => sum + Math.pow(area - mean, 2), 0) / areas.length
            : 0
    const stdDev = Math.sqrt(variance)
    const centerIndex = Math.ceil(hotspotSizes.length / 2)

    const getSizeIndex = (area: number): number => {
        // If all areas are equal, they should all be the medium/middle size.
        if (stdDev === 0) return centerIndex

        // Number of standard deviations from the mean.
        const z = (area - mean) / stdDev

        const index = Math.round(centerIndex + z * 8)

        // Clamp the index to the range of hotspotSizes.
        return Math.max(0, Math.min(hotspotSizes.length - 1, index))
    }

    const filtered = hotspots
        .filter((hotspot) => {
            let isWithinSizeRange = true
            if (sizeRange && hotspot.area !== undefined) {
                if (sizeRange.min !== undefined) {
                    if (sizeRange.min > hotspot.area) isWithinSizeRange = false
                }

                if (sizeRange.max !== undefined) {
                    if (sizeRange.max < hotspot.area) isWithinSizeRange = false
                }
            }

            let isWithinTemperatureRange = true
            if (temperatureRange && hotspot.temperature !== undefined) {
                if (temperatureRange.min !== undefined) {
                    if (temperatureRange.min > hotspot.temperature) isWithinTemperatureRange = false
                }

                if (temperatureRange.max !== undefined) {
                    if (temperatureRange.max < hotspot.temperature) isWithinTemperatureRange = false
                }
            }

            return isWithinSizeRange && isWithinTemperatureRange
        })
        .map((hotspot) => {
            const bbArea = getBoundingBoxArea(hotspot.polygon) ?? hotspot.area
            return bbArea != null ? { ...hotspot, sizeIndex: getSizeIndex(bbArea) } : hotspot
        }) as HotspotWithDistance[]

    return filtered
}

/** @deprecated */
export const filterHotspots = (
    properties: HotspotProperties,
    hotspots: HotspotWithContext[],
    distances: Record<string, number | undefined>,
    distanceToPerimeterIsEnabled = true,
    sizeRange?: MinMax
) => {
    const areas = hotspots
        .map((h) => getBoundingBoxArea(h.polygon) ?? h.area)
        .filter((area) => area !== undefined)
    const mean = areas.reduce((sum, a) => sum + a, 0) / areas.length
    const variance = areas.reduce((sum, a) => sum + Math.pow(a - mean, 2), 0) / areas.length
    const stdDev = Math.sqrt(variance)

    function getSizeIndex(area: number): number {
        if (stdDev === 0) return 2 // all areas are equal — default to middle bin

        const z = (area - mean) / stdDev // how many std devs from mean
        const centerIndex = Math.floor(hotspotSizes.length / 2) // 5 bins: index 0-4 → center is index 2
        const index = Math.round(centerIndex + z * 8)

        return Math.max(0, Math.min(hotspotSizes.length - 1, index))
    }

    return hotspots
        .map((d) => {
            const distanceToPerimeter = distances[d.id]

            //filter based on the values in the Distance to Perimeter tool
            let isWithinPerimeter = true
            const ranges = properties?.perimeterDistance?.range

            if (ranges?.length && distanceToPerimeter && distanceToPerimeterIsEnabled) {
                const maxDistanceRange = ranges[ranges.length - 1]
                isWithinPerimeter = distanceToPerimeter < maxDistanceRange.distance
            }
            //filter based on confidence and the sensitivity slider
            const confidence = d.confidence
            const sensitivity = properties?.sensitivity
            let isWithinSensitivity = true
            if (confidence && sensitivity) {
                isWithinSensitivity = confidence >= sensitivity
            }

            let isWithinSizeRange = true
            if (sizeRange && d.area !== undefined) {
                if (sizeRange.min !== undefined) {
                    if (sizeRange.min > d.area) isWithinSizeRange = false
                }

                if (sizeRange.max !== undefined) {
                    if (sizeRange.max < d.area) isWithinSizeRange = false
                }
            }

            // This code filters by polygons instead of individual hotspot area:
            // const polygonSize = d.polygon.length >= 3 && calculateArea(d.polygon)
            // const isWithinSizeRange =
            //     sizeRange && polygonSize
            //         ? polygonSize >= sizeRange.min && polygonSize <= sizeRange.max
            //         : false

            if (isWithinPerimeter && isWithinSensitivity && isWithinSizeRange) {
                const bbArea = getBoundingBoxArea(d.polygon) ?? d.area
                const hotspotWithDistance: HotspotWithDistance = {
                    ...d,
                    distanceToPerimeter: distances[d.id],
                    sizeIndex: bbArea != null ? getSizeIndex(bbArea) : 0,
                } as HotspotWithDistance
                const color = getHotspotFillColor(
                    hotspotWithDistance,
                    properties,
                    undefined,
                    distanceToPerimeterIsEnabled
                )
                hotspotWithDistance.color = color
                return hotspotWithDistance
            }
        })
        .filter(Boolean) as HotspotWithDistance[]
}

export const sortHotspotsAndAssignIds = (flight: InfraredFlightData) => {
    const sorted = [...flight.hotspots].sort((a, b) => (a.id < b.id ? -1 : 1))

    const result = sorted.map((hotspot) => ({
        ...hotspot,
        flightId: flight.id,
        isManuallyUploaded: flight.skippedProcessing,
    })) as HotspotWithContext[]

    return result
}

export const sortHotspotsAndAssignLabels = (hotspots: HotspotWithContext[]) => {
    const sorted = sortByDbscanClusters(hotspots)
    return sorted.map((hotspot, i) => ({ ...hotspot, label: `${i + 1}` })) as HotspotWithLabel[]
}

export const getColoredFilteredHotspotsForMergedFlights = (
    flights: InfraredFlightData[],
    project: Project
) => {
    const firePerimeterPolygons = project.perimeters
        .filter((e) => e.visible && !e.archivedTimestamp)
        ?.flatMap((e) => e.polygons)

    const allHotspots: HotspotWithContext[] = flights.flatMap((flight) =>
        sortHotspotsAndAssignIds(flight).filter((e) => e.status !== HotspotStatus.Removed)
    )

    const distances = getHotspotPerimeterDistances(allHotspots, firePerimeterPolygons) || []
    const distancesById = allHotspots.reduce(
        (acc, cur, i) => {
            acc[cur.id] = distances[i]
            return acc
        },
        {} as Record<string, number | undefined>
    )

    // Use the first flight's sensitivity/filter settings for the combined set
    const primaryFlight = flights[0]
    const hotspotsFilteredBySensitivity = filterHotspotsBySensitivity({
        sensitivity: primaryFlight.sensitivity,
        hotspots: allHotspots,
    })
    const hotspotsFilteredBySensitivityAndDistance = filterHotspotsByDistance({
        distances: distancesById,
        properties: primaryFlight,
        distanceToPerimeterIsEnabled:
            primaryFlight.filters?.perimeter !== undefined ? primaryFlight.filters.perimeter : true,
        hotspots: hotspotsFilteredBySensitivity,
        needsLabel: true,
    })
    const hotspotsFilteredByGraphs = filterHotspotsByGraphs({
        hotspotsFilteredBySensitivity,
        hotspotsFilteredBySensitivityAndDistance,
        sizeRange: primaryFlight.filters?.size,
        temperatureRange: primaryFlight.filters?.temperature,
    })

    return sortHotspotsAndAssignLabels(hotspotsFilteredByGraphs)
}

export const getColoredFilteredHotspotsByFlightId = (flightId: string, project: Project) => {
    const flight = project.infraredFlights.find((flight) => flight.id === flightId)
    if (!flight) throw Error(`No flight found with ID: ${flightId}.`)

    const firePerimeterPolygons = project.perimeters
        .filter((e) => e.visible && !e.archivedTimestamp)
        ?.flatMap((e) => e.polygons)
    const hotspotsWithContext: HotspotWithContext[] = sortHotspotsAndAssignIds(flight).filter(
        (e) => e.status !== HotspotStatus.Removed
    )
    const distances = getHotspotPerimeterDistances(hotspotsWithContext, firePerimeterPolygons) || []
    const distancesById = hotspotsWithContext.reduce(
        (acc, cur, i) => {
            acc[cur.id] = distances[i]
            return acc
        },
        {} as Record<string, number | undefined>
    )

    const hotspotsFilteredBySensitivity = filterHotspotsBySensitivity({
        sensitivity: flight.sensitivity,
        hotspots: hotspotsWithContext,
    })
    const hotspotsFilteredBySensitivityAndDistance = filterHotspotsByDistance({
        distances: distancesById,
        properties: flight,
        distanceToPerimeterIsEnabled:
            flight.filters?.perimeter !== undefined ? flight.filters.perimeter : true,
        hotspots: hotspotsFilteredBySensitivity,
        needsLabel: true,
    })
    const hotspotsFilteredByGraphs = filterHotspotsByGraphs({
        hotspotsFilteredBySensitivity,
        hotspotsFilteredBySensitivityAndDistance,
        sizeRange: flight.filters?.size,
        temperatureRange: flight.filters?.temperature,
    })
    const hotspotsWithLabels: HotspotWithLabel[] =
        sortHotspotsAndAssignLabels(hotspotsFilteredByGraphs)

    return hotspotsWithLabels
}
